// Exécuteur des prises (spec anti-perte phase 1, §5.2) : lit chaque prise en
// attente sur le téléphone, demande à la machine à états (`take-lifecycle.ts`)
// quoi faire, et le fait — relancer la fusion, créer la capture, confirmer
// l'envoi puis libérer, passer au dernier recours serveur, mettre à la
// corbeille, proposer la reprise — et émet les signalements automatiques.
//
// C'est la SEULE pièce de l'app qui déclenche une libération (suppression) de
// prise : `releaseTake` n'est appelé qu'après une confirmation serveur, soit
// (chemin normal) un fichier fusionné qui contient CHAQUE segment mesuré et
// `confirmUploaded` vrai, soit la décision `release` d'A2 (ligne serveur
// `captured` dont la durée couvre les segments). Invariant I1.
import type { SupabaseClient } from "@supabase/supabase-js";
import type { TakeRecorder } from "../native/take-recorder";
import { buildCapture } from "./build-capture";
import { isIncomplete } from "./incomplete";
import type { Capture } from "./schema";
import { type HandOffResult, plausibleElapsedMs } from "./segment-upload";
import { makeSingleFlight } from "./single-flight";
import type { CaptureStorage } from "./storage";
import {
  DURATION_TOLERANCE_MS_PER_SEGMENT,
  decideTakeAction,
  hasSyncFailure,
  hasSyncProblem,
  type ServerCaptureView,
  syncClearedError,
  type TakeAction,
} from "./take-lifecycle";
import { parseTakeMeta, takeMeetingContext } from "./take-meta";
import type { TakeErrorInfo, TakeManifest, TakeMeta } from "./take-types";
import { BUCKET } from "./upload";

export interface RunnerDeps {
  recorder: TakeRecorder;
  /**
   * Ligne `captures` du serveur, lue sous la session de `userId`. `null` =
   * lecture RÉUSSIE et ligne absente ; une lecture en échec, sans session ou
   * sous un autre compte doit REJETER (sinon A2 mettrait la prise à la corbeille).
   */
  fetchServerCapture: (captureId: string, userId: string) => Promise<ServerCaptureView | null>;
  /**
   * Seconde preuve d'une capture supprimée, lue sous la session de `userId` :
   * vrai seulement si ni `<userId>/<captureId>.m4a` ni aucun segment de
   * `<userId>/<captureId>/segments/` n'existe. Une lecture en échec REJETTE
   * (jamais « absent »). Sans elle, une ligne masquée par la RLS de projet
   * enverrait la prise à la corbeille.
   */
  serverAudioAbsent: (captureId: string, userId: string) => Promise<boolean>;
  /** `{ ok: true }` seulement si la capture est maintenant sur le serveur. */
  createCapture: (
    m: TakeManifest,
    meta: TakeMeta,
    merged: { uri: string; measuredMs: number; unreadable: string[] },
  ) => Promise<{
    ok: boolean;
    /** Une capture locale de ce `captureId` attend déjà sa synchro : rien recréé. */
    localPending?: boolean;
  }>;
  confirmUploaded: (m: TakeManifest) => Promise<boolean>;
  handOff: (m: TakeManifest, meta: TakeMeta) => Promise<HandOffResult>;
  /**
   * Dédoublonne par prise et par état (`createAutoSignaleur`). Jamais attendu
   * par l'exécuteur : un envoi lent ne retient ni la prise ni `runAll`.
   */
  signaler: (kind: "incomplete" | "problem", m: TakeManifest) => Promise<void>;
  isOnline: () => boolean;
  sleep: (ms: number) => Promise<void>;
  /** Rencontre interrompue (app tuée pendant la prise) : proposer de la reprendre. */
  offerResume: (takeId: string) => void;
  /**
   * La prise complète une capture EXISTANTE (C25, capture tronquée du 24-09) :
   * un fichier fusionné partiel ne la laisse jamais `retained` (tronquée pour
   * toujours) — le serveur réassemble à partir des segments. Absent = jamais.
   */
  completesExistingCapture?: (m: TakeManifest) => Promise<boolean>;
}

/** Émis sur `window` après chaque action qui change un manifeste. */
export const TAKES_CHANGED_EVENT = "captureBesoins.takesChanged";

/** Tours au plus par passage : garde contre une étape qui n'avancerait pas. */
export const MAX_TURNS_PER_PASS = 6;

function notifyTakesChanged(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(TAKES_CHANGED_EVENT));
}

interface ServerRead {
  server: ServerCaptureView | null;
  serverChecked: boolean;
  serverAudioAbsent?: boolean;
}

const UNREAD: ServerRead = { server: null, serverChecked: false };

// La ligne serveur ne compte que pour une prise fusionnée ou confiée au
// serveur. `serverChecked` n'est vrai que si la lecture a RÉUSSI sous le compte
// de la prise : une ligne « absente » met la prise à la corbeille (A2), jamais
// une panne de réseau ni une session absente ou étrangère.
async function readServer(deps: RunnerDeps, m: TakeManifest): Promise<ServerRead> {
  if (m.active === true || !m.captureId) return UNREAD;
  if (m.state !== "merged" && m.state !== "handed_to_server" && m.state !== "retained") {
    return UNREAD;
  }
  const meta = parseTakeMeta(m.meta);
  if (!meta || !deps.isOnline()) return UNREAD;
  try {
    const server = await deps.fetchServerCapture(m.captureId, meta.userId);
    if (server || m.state === "merged") return { server, serverChecked: true };
    // Prise confiée au serveur ou gardée, ligne absente : la corbeille exige
    // une seconde preuve, l'absence de tout audio dans le dossier Storage.
    const serverAudioAbsent = await deps.serverAudioAbsent(m.captureId, meta.userId);
    return { server: null, serverChecked: true, serverAudioAbsent };
  } catch {
    return UNREAD;
  }
}

// Le signalement part en arrière-plan : la file du signaleur garde l'ordre et
// le dédoublonnage, l'exécuteur n'attend jamais le réseau de l'Edge Function.
function fireSignal(deps: RunnerDeps, kind: "incomplete" | "problem", m: TakeManifest): void {
  void Promise.resolve()
    .then(() => deps.signaler(kind, m))
    .catch(() => {});
}

// Capture assemblée par le serveur (dernier recours) mais incomplète : audio
// illisible estimé, ou durée sous le seuil de l'horloge de la prise.
function assembledIncomplete(m: TakeManifest, server: ServerCaptureView | null): boolean {
  if (m.state !== "handed_to_server" || server?.status !== "captured") return false;
  if ((server.missingAudioMs ?? 0) > 0) return true;
  return server.durationS != null && isIncomplete(server.durationS * 1000, m.elapsedMs);
}

function signalServerState(deps: RunnerDeps, m: TakeManifest, server: ServerCaptureView | null) {
  // Assemblage serveur en échec (C08).
  if (server?.status === "assembly_failed") fireSignal(deps, "problem", m);
  if (assembledIncomplete(m, server)) fireSignal(deps, "incomplete", m);
}

/** Mémoire d'un passage sur une prise. */
interface Pass {
  deps: RunnerDeps;
  takeId: string;
  /** Segments illisibles de la dernière fusion réussie (le manifeste ne les garde pas). */
  unreadable: string[];
  /** Une seule création de capture par passage : jamais deux envois du même audio. */
  captureAttempted: boolean;
  /** La personne a choisi « Sauver tel quel » : une rencontre interrompue est fermée, pas reproposée. */
  declineResume: boolean;
}

type Step = "continue" | "stop";

// Revue finale N-I2 (CA8) : un échec d'envoi ou d'appel de l'exécuteur est
// inscrit au manifeste (`lastError`) et au journal natif joint aux
// signalements. Best-effort : jamais bloquant.
async function reportFailure(pass: Pass, error: TakeErrorInfo): Promise<void> {
  await pass.deps.recorder.markTake({ takeId: pass.takeId, error }).catch(() => undefined);
}

async function merge(pass: Pass, delayMs: number): Promise<Step> {
  if (delayMs > 0) await pass.deps.sleep(delayMs);
  const r = await pass.deps.recorder.retryMerge(pass.takeId);
  notifyTakesChanged();
  if (r.status === "merged") pass.unreadable = r.unreadable;
  return "continue";
}

// Prise adoptée (orphelins) : pas de `captureId`. On en pose un avant de créer
// ou d'envoyer quoi que ce soit, pour que la capture et le dossier Storage
// portent toujours l'identifiant du manifeste.
async function assignCaptureId(pass: Pass): Promise<TakeManifest> {
  const updated = await pass.deps.recorder.markTake({
    takeId: pass.takeId,
    captureId: crypto.randomUUID(),
  });
  notifyTakesChanged();
  return updated;
}

async function createCaptureStep(pass: Pass, m: TakeManifest, read: ServerRead): Promise<Step> {
  if (!m.captureId) {
    await assignCaptureId(pass);
    return "continue";
  }
  // Serveur non lu (hors ligne, panne) : on ne sait pas si la capture y est
  // déjà — ne rien créer, le prochain passage relira.
  if (!read.serverChecked || pass.captureAttempted) return "stop";
  pass.captureAttempted = true;
  // Métadonnées illisibles (C13) : le sélecteur de projet de l'accueil prend le relais.
  const meta = parseTakeMeta(m.meta);
  if (!meta) return "stop";
  // Fusion plus courte que ses segments mesurés (C10) : c'est une fusion ratée.
  // Aucune capture depuis ce fichier (I2) : dernier recours, le serveur
  // réassemble à partir des segments et mesure lui-même. S'il échoue à son
  // tour, le signalement « problem » part par le chemin habituel (C08).
  if (mergeFellShort(m)) return handOffStep(pass, withoutFailedMerge(m));
  const files = await pass.deps.recorder.getTakeSegments(m.takeId);
  if (!files.merged || !m.merged) return "stop";
  const measuredMs = m.merged.durationMs;
  const unreadable = pass.unreadable;
  const created = await pass.deps.createCapture(m, meta, {
    uri: files.merged.uri,
    measuredMs,
    unreadable,
  });
  // Audio manquant : même source que `missingAudioMs` (segments rendus
  // illisibles par la fusion, ou non mesurés au manifeste).
  if (isIncomplete(measuredMs, m.elapsedMs) || takeMissingAudioMs(m, unreadable) !== null) {
    fireSignal(pass.deps, "incomplete", m);
  }
  if (created.ok) return "continue";
  // Capture locale refusée à l'envoi (classe « problem » inscrite par l'écran,
  // C20) : l'équipe est prévenue à chaque passage tant qu'elle attend — le
  // signaleur dédoublonne par prise, un renvoi après un envoi réussi est
  // gratuit. Cette erreur de synchro n'est jamais écrasée par « capture non créée ».
  if (hasSyncProblem(m)) {
    if (created.localPending) fireSignal(pass.deps, "problem", m);
    return "stop";
  }
  await reportFailure(pass, {
    domain: "Ecoute.createCapture",
    code: -1,
    message: "capture non créée (sauvegarde ou synchro en échec)",
  });
  return "stop";
}

// La capture est arrivée sur le serveur : un échec de synchro encore inscrit
// au manifeste est levé (la prise suit son cours jusqu'à la libération sur
// preuve). Best-effort.
async function clearSyncFailure(pass: Pass, m: TakeManifest, read: ServerRead): Promise<void> {
  if (!read.server || !hasSyncFailure(m)) return;
  await pass.deps.recorder
    .markTake({ takeId: m.takeId, error: syncClearedError() })
    .catch(() => undefined);
}

// Le fichier fusionné est-il au moins aussi long que les segments mesurés
// (tolérance §5.3) ? Sinon la fusion a rompu son contrat (C10).
function mergedCoversMeasured(m: TakeManifest): boolean {
  if (!m.merged) return false;
  const measuredMs = m.segments.reduce((t, s) => t + (s.durationMs ?? 0), 0);
  return m.merged.durationMs >= measuredMs - DURATION_TOLERANCE_MS_PER_SEGMENT * m.segments.length;
}

/**
 * Fusion plus courte que ses segments mesurés (C10, R43) : aucune capture ne
 * doit être créée depuis ce fichier — ni par l'exécuteur, ni par l'écran de
 * capture, qui réutilise cette règle.
 */
export function mergeFellShort(m: TakeManifest): boolean {
  return m.merged !== undefined && !mergedCoversMeasured(m);
}

// Une fusion ratée ne compte pas : la durée conservée redevient la somme des
// segments, pour le dernier recours (durée attendue) comme pour la décision de
// libération qui suit l'assemblage serveur (A2 lit `conservedMs`).
function withoutFailedMerge(m: TakeManifest): TakeManifest {
  return mergeFellShort(m) ? { ...m, merged: undefined } : m;
}

// Le fichier fusionné contient-il TOUT l'audio de la prise ? Le natif mesure
// chaque segment avant la fusion (`durationMs` absent = illisible, R2). Un
// segment non mesuré, ou une fusion plus courte que la somme, et le fichier
// envoyé n'est pas toute la prise : les segments restent seuls détenteurs d'une
// partie de l'audio (C09, C10).
function mergedHoldsEverySegment(m: TakeManifest): boolean {
  return m.segments.every((s) => typeof s.durationMs === "number") && mergedCoversMeasured(m);
}

async function confirmStep(pass: Pass, m: TakeManifest, read: ServerRead): Promise<Step> {
  if (!mergedHoldsEverySegment(m)) {
    // Capture tronquée à compléter (C25) : son audio actuel n'est PAS celui de
    // cette prise — la garder la laisserait tronquée. Dernier recours serveur.
    if (await pass.deps.completesExistingCapture?.(m)) {
      return handOffStep(pass, withoutFailedMerge(m));
    }
    // Fichier fusionné partiel : jamais libérée. La capture existe sur le
    // serveur AVEC son audio : la prise passe en `retained` (C09) — une capture
    // supprimée ensuite part à la corbeille au lieu d'être recréée et renvoyée.
    // Une ligne sans audio (dernier recours en échec, `assembly_failed`) n'est
    // pas une capture écoutable : la prise reste telle quelle.
    if (read.server?.audioPath != null) {
      await pass.deps.recorder.markTake({ takeId: m.takeId, state: "retained" });
      notifyTakesChanged();
    }
    return "stop";
  }
  // Confirmation fausse : rien libéré, le prochain passage réessaie (pas 6 tours).
  if (!(await pass.deps.confirmUploaded(m))) {
    await reportFailure(pass, {
      domain: "Ecoute.confirmUpload",
      code: -1,
      message: "envoi non confirmé (objet Storage absent ou de taille différente)",
    });
    return "stop";
  }
  await pass.deps.recorder.releaseTake(m.takeId);
  notifyTakesChanged();
  return "continue";
}

// `offline`, `no_project` et `no_capture` rendent la main avant de toucher au
// manifeste ; toute autre issue a pu le changer (état, segments envoyés).
function handOffTouchedManifest(r: HandOffResult): boolean {
  return r.ok || !["offline", "no_project", "no_capture"].includes(r.reason);
}

async function handOffStep(pass: Pass, m: TakeManifest): Promise<Step> {
  const meta = parseTakeMeta(m.meta);
  if (!meta) return "stop";
  let r = await pass.deps.handOff(m, meta);
  if (!r.ok && r.reason === "no_capture") {
    // Prise adoptée sans `captureId` : on en pose un, puis un seul nouvel appel.
    r = await pass.deps.handOff(await assignCaptureId(pass), meta);
  }
  if (handOffTouchedManifest(r)) notifyTakesChanged();
  // Hors ligne ou sans projet : arrêt. Autre échec : le prochain passage reprend
  // là où l'envoi s'est arrêté (segments déjà reçus jamais renvoyés, A4).
  return r.ok ? "continue" : "stop";
}

async function act(
  pass: Pass,
  m: TakeManifest,
  read: ServerRead,
  action: TakeAction,
): Promise<Step> {
  const { recorder } = pass.deps;
  switch (action.kind) {
    case "none":
    case "await_server":
      return "stop";
    case "offer_resume":
      // « Sauver tel quel » : la fusion se fait ici, sous le verrou de la prise.
      if (pass.declineResume) return merge(pass, 0);
      pass.deps.offerResume(m.takeId);
      return "stop";
    case "close_and_merge":
      return merge(pass, 0);
    case "retry_merge":
      return merge(pass, action.delayMs);
    case "create_capture":
      return createCaptureStep(pass, m, read);
    case "confirm_upload":
      return confirmStep(pass, m, read);
    case "hand_off":
      // Durée attendue et `duration_s` : toujours la somme des segments.
      return handOffStep(pass, withoutFailedMerge(m));
    case "release":
      await recorder.releaseTake(m.takeId);
      notifyTakesChanged();
      return "continue";
    case "trash":
      await recorder.trashTake(m.takeId);
      notifyTakesChanged();
      return "continue";
  }
}

async function runPass(deps: RunnerDeps, takeId: string, opts: RunTakeOptions): Promise<void> {
  const pass: Pass = {
    deps,
    takeId,
    unreadable: [],
    captureAttempted: false,
    declineResume: opts.declineResume === true,
  };
  try {
    for (let turn = 0; turn < MAX_TURNS_PER_PASS; turn++) {
      const { takes } = await deps.recorder.listTakes();
      const m = takes.find((t) => t.takeId === takeId);
      // Libérée, à la corbeille, ou introuvable : plus rien à faire.
      if (!m) return;
      const read = await readServer(deps, m);
      signalServerState(deps, m, read.server);
      await clearSyncFailure(pass, m, read);
      const action = decideTakeAction(withoutFailedMerge(m), {
        isActive: m.active === true,
        ...read,
      });
      if ((await act(pass, m, read, action)) === "stop") return;
    }
  } catch (err) {
    // Jamais d'exception vers l'écran ou le filet : rien n'est libéré, le
    // prochain passage (lancement, réseau, premier plan) réessaie.
    console.warn(`[take-runner] prise ${takeId} : passage interrompu`, err);
  }
}

export interface RunTakeOptions {
  /**
   * « Sauver tel quel » (écran de capture) : une rencontre interrompue est
   * fermée et fusionnée au lieu d'être proposée à la reprise.
   */
  declineResume?: boolean;
}

// Un seul passage en vol PAR prise, quel que soit l'appelant (écran de capture,
// `runAll`) : un appel concurrent rend la promesse du passage en cours. Un
// « Sauver tel quel » n'est jamais absorbé par un passage qui proposerait la
// reprise : il repart après lui, toujours sous le verrou.
const inFlight = new Map<string, Promise<void>>();

export function runTake(
  deps: RunnerDeps,
  takeId: string,
  opts: RunTakeOptions = {},
): Promise<void> {
  const current = inFlight.get(takeId);
  if (current) {
    return opts.declineResume ? current.then(() => runTake(deps, takeId, opts)) : current;
  }
  const pass = runPass(deps, takeId, opts).finally(() => inFlight.delete(takeId));
  inFlight.set(takeId, pass);
  return pass;
}

export function createTakeRunner(deps: RunnerDeps): { runAll: () => Promise<void> } {
  const singleFlight = makeSingleFlight();
  return {
    runAll: async () => {
      try {
        await singleFlight(async () => {
          const { takes } = await deps.recorder.listTakes();
          // Une prise après l'autre : chacune garde ses segments, son état, sa capture.
          for (const t of takes) await runTake(deps, t.takeId);
        });
      } catch (err) {
        console.warn("[take-runner] passage global interrompu", err);
      }
    },
  };
}

// ─── Dépendances de production ───────────────────────────────────────────────

interface CaptureRowLite {
  status: string;
  duration_s: number | null;
  audio_path: string | null;
  missing_audio_ms?: number | null;
  assembly_manifest?: unknown;
}

// Sans session (ou sous un autre compte), la RLS rendrait 0 ligne et un dossier
// Storage vide : les deux seraient lus « capture supprimée ». On lève.
async function assertSessionOf(client: Pick<SupabaseClient, "auth">, userId: string) {
  const { data: auth, error: authError } = await client.auth.getSession();
  if (authError) throw new Error(authError.message);
  const sessionUserId = auth.session?.user.id;
  if (!sessionUserId) throw new Error("aucune session : serveur non lu");
  if (sessionUserId !== userId) throw new Error("autre compte connecté : serveur non lu");
}

/** Preuve d'assemblage écrite par `assemble-capture` à sa réussite ; `null` sinon. */
export function assembledMsOf(manifest: unknown): number | null {
  if (!manifest || typeof manifest !== "object") return null;
  const v = (manifest as { assembledMs?: unknown }).assembledMs;
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
}

/**
 * `fetchServerCapture` réel : `null` seulement si la lecture réussit sans ligne,
 * sous la session du propriétaire de la prise. Sans session (ou sous un autre
 * compte), la RLS rendrait 0 ligne, lue comme « capture supprimée » : on lève.
 */
export function createServerCaptureFetcher(
  client: Pick<SupabaseClient, "from" | "auth">,
): RunnerDeps["fetchServerCapture"] {
  return async (captureId, userId) => {
    await assertSessionOf(client, userId);
    const { data, error } = await client
      .from("captures")
      .select("status, duration_s, audio_path, missing_audio_ms, assembly_manifest")
      .eq("id", captureId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    const row = data as CaptureRowLite | null;
    if (!row) return null;
    return {
      status: row.status,
      durationS: row.duration_s,
      audioPath: row.audio_path,
      missingAudioMs: row.missing_audio_ms ?? null,
      assembledMs: assembledMsOf(row.assembly_manifest),
    };
  };
}

/**
 * `serverAudioAbsent` réel (seconde preuve avant la corbeille) : dans le dossier
 * de la personne (lecture permise par `captures_audio_read`, quelle que soit la
 * visibilité du projet), ni `<captureId>.m4a` ni aucun objet sous
 * `<captureId>/segments/`. Session vérifiée avant ET après les deux lectures ;
 * toute erreur lève (jamais « absent »).
 */
export function createServerAudioProbe(
  client: Pick<SupabaseClient, "storage" | "auth">,
): RunnerDeps["serverAudioAbsent"] {
  return async (captureId, userId) => {
    await assertSessionOf(client, userId);
    const bucket = client.storage.from(BUCKET);
    const mergedName = `${captureId}.m4a`;
    const root = await bucket.list(userId, { search: mergedName });
    if (root.error || !root.data) throw new Error(root.error?.message ?? "liste Storage vide");
    if (root.data.some((f) => f.name === mergedName)) return false;
    const segments = await bucket.list(`${userId}/${captureId}/segments`, { limit: 1 });
    if (segments.error || !segments.data) {
      throw new Error(segments.error?.message ?? "liste Storage vide");
    }
    if (segments.data.length > 0) return false;
    await assertSessionOf(client, userId);
    return true;
  };
}

/**
 * `confirmUploaded` réel (écart assumé 4 du plan) : l'objet Storage
 * `<userId>/<captureId>.m4a` (cf. `buildAudioStoragePath`) existe et sa taille
 * égale celle du fichier fusionné local. Tout doute rend `false` : rien libéré.
 */
export function createUploadConfirmer(o: {
  client: Pick<SupabaseClient, "storage">;
  recorder: TakeRecorder;
}): RunnerDeps["confirmUploaded"] {
  return async (m) => {
    const meta = parseTakeMeta(m.meta);
    if (!meta || !m.captureId) return false;
    try {
      const files = await o.recorder.getTakeSegments(m.takeId);
      if (!files.merged) return false;
      const name = `${m.captureId}.m4a`;
      const { data, error } = await o.client.storage
        .from(BUCKET)
        .list(meta.userId, { search: name });
      if (error || !data) return false;
      const size = data.find((f) => f.name === name)?.metadata?.size;
      return typeof size === "number" && size === files.merged.bytes;
    } catch {
      return false;
    }
  };
}

export interface TakeCaptureCreatorDeps {
  storage: Pick<CaptureStorage, "list" | "save" | "remove">;
  /** `syncCaptureToServer` lié au client et à la personne connectée. */
  sync: (capture: Capture) => Promise<{ ok: boolean }>;
  now?: () => Date;
}

/** Durée d'horloge de la prise en secondes, bornée comme le dernier recours (A4). */
export function takeElapsedS(m: TakeManifest): number | null {
  const ms = plausibleElapsedMs(m.elapsedMs);
  return ms == null ? null : Math.floor(ms / 1000);
}

/** Durée comptée pour un segment illisible dont la taille est inconnue : une rotation. */
export const MISSING_SEGMENT_FLOOR_MS = 30_000;

/**
 * Audio illisible estimé (ms, 32 kbps = 4 octets/ms) : segments rendus
 * illisibles par la fusion, ou non mesurés au manifeste. `null` s'il n'y en a pas.
 */
export function takeMissingAudioMs(m: TakeManifest, unreadable: string[]): number | null {
  const names = new Set(unreadable);
  const missing = m.segments.filter((s) => names.has(s.name) || typeof s.durationMs !== "number");
  if (missing.length === 0) return null;
  // Taille inconnue : un segment de rotation (30 s), jamais 0 — sinon la
  // capture s'afficherait complète alors que le signalement dit l'inverse (I2).
  const totalMs = missing.reduce(
    (t, s) => t + (typeof s.bytes === "number" ? s.bytes / 4 : MISSING_SEGMENT_FLOOR_MS),
    0,
  );
  return Math.max(1, Math.floor(totalMs));
}

function startedAtOf(meta: TakeMeta, m: TakeManifest, fallback: Date): Date {
  for (const iso of [meta.startedAt, m.startedAt]) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return fallback;
}

// Réexportée ici pour ne casser aucun appelant : la fonction vit dans
// `take-meta.ts` (le dernier recours `segment-upload.ts` en a besoin aussi).
export { takeMeetingContext } from "./take-meta";

export interface CaptureFromTakeInput {
  m: TakeManifest;
  meta: TakeMeta;
  captureId: string;
  projectId: string;
  createdBy: string;
  merged: { uri: string; measuredMs: number; unreadable: string[] };
  now: Date;
}

/**
 * Capture d'une prise fusionnée — la SEULE construction, pour l'exécuteur comme
 * pour l'écran de capture : durée = durée MESURÉE du fichier fusionné (C26,
 * jamais un chrono), durée d'horloge et audio illisible estimé (« Incomplète »,
 * I2), départ lu dans les métadonnées puis le manifeste.
 */
export function captureFromTake(o: CaptureFromTakeInput): Capture {
  return buildCapture({
    id: o.captureId,
    createdBy: o.createdBy,
    projectId: o.projectId,
    type: o.meta.type,
    title: o.meta.title ?? null,
    durationMs: o.merged.measuredMs,
    audioUri: o.merged.uri,
    keepAudio: true,
    startedAt: startedAtOf(o.meta, o.m, o.now),
    now: o.now,
    meetingContext: takeMeetingContext(o.meta),
    batchExtraction: o.meta.batchExtraction,
    elapsedS: takeElapsedS(o.m),
    missingAudioMs: takeMissingAudioMs(o.m, o.merged.unreadable),
  });
}

/**
 * `createCapture` réel (chemin normal) : capture `id = m.captureId`, durée =
 * durée mesurée du fichier fusionné, durée d'horloge et audio illisible estimé
 * (« Incomplète », I2), sauvegarde locale puis synchro. Idempotent
 * par `captureId` : une capture locale qui porte déjà cet id (créée par l'écran
 * de capture, ou synchro en attente) n'est jamais recréée — la synchro
 * existante s'en charge.
 */
export function createTakeCaptureCreator(o: TakeCaptureCreatorDeps): RunnerDeps["createCapture"] {
  const now = o.now ?? (() => new Date());
  return async (m, meta, merged) => {
    const captureId = m.captureId;
    const projectId = meta.projectId;
    if (!captureId || !projectId) return { ok: false };
    try {
      if ((await o.storage.list()).some((c) => c.id === captureId)) {
        return { ok: false, localPending: true };
      }
      const capture = captureFromTake({
        m,
        meta,
        captureId,
        projectId,
        createdBy: meta.userId,
        merged,
        now: now(),
      });
      await o.storage.save(capture);
      // Synchro en échec : la capture reste en local, le filet de re-synchro reprend.
      if (!(await o.sync(capture)).ok) return { ok: false };
      await o.storage.remove(capture.id);
      return { ok: true };
    } catch (err) {
      console.warn(`[take-runner] capture ${captureId} non créée`, err);
      return { ok: false };
    }
  };
}
